import { Box } from "@mui/material";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ScrollRestoration, useLocation, useNavigate, useParams } from "react-router-dom";
import "./App.css";
import Footer from "./Components/Footer/Footer";
import Header from "./Components/Header/Header";
import MainContent from "./Components/MainContent/MainContent";
import { setTitle } from "./redux/actions/dashboardTitle";
import { useIsAuthenticated } from "@azure/msal-react";
import { Loader } from "./CommonComponents/common/Loader";
import SideDrawer from "./Pages/ControlTower/SideDrawer/SideDrawer";
import { LicenseManager } from "ag-grid-enterprise";
import { appRootUrl, controlTowerMenuItems } from "./routes/PrivateRoutes";
import AccessRequestForm from "./Pages/ControlTower/UserRoles/AccessRequestForm";
import { fetchUserAccess } from "./services/OutboundOrders/UserRolesServices";
import { setApprovedRegions, setApprovedPlants, setRegionPlantMapping } from "./redux/userSlice";
import { setCtAppRoles } from './redux/userSlice';
import SupportButton from "./CommonComponents/Support/SupportButton";
import SupportTicketDialog from "./CommonComponents/Support/SupportTicketDialog";
import FilterDrawer from "./Pages/ControlTower/CTDashboard/FilterDrawer";

LicenseManager.setLicenseKey("Using_this_{AG_Charts_and_AG_Grid}_Enterprise_key_{AG-065772}_in_excess_of_the_licence_granted_is_not_permitted___Please_report_misuse_to_legal@ag-grid.com___For_help_with_changing_this_key_please_contact_info@ag-grid.com___{The_Coca_Cola_Company}_is_granted_a_{Single_Application}_Developer_License_for_the_application_{Topline_Forecast}_only_for_{6}_Front-End_JavaScript_developers___All_Front-End_JavaScript_developers_working_on_{Topline_Forecast}_need_to_be_licensed___{Topline_Forecast}_has_not_been_granted_a_Deployment_License_Add-on___This_key_works_with_{AG_Charts_and_AG_Grid}_Enterprise_versions_released_before_{21_September_2025}____[v3]_[0102]_MTc1ODQwOTIwMDAwMA==372521b73ce277d3fad471863dd69994");

function App() {
  const headerRef = useRef(null);
  const isAuthenticated = useIsAuthenticated();
  const [headerHeight, setHeaderHeight] = useState(0);
  const [hasAccess, setHasAccess] = useState(null);
  const [ctAppRole, setCtAppRole] = useState([]);
  const [openSupport, setOpenSupport] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const tabDetails = useSelector((state) => state?.dashboardDetails?.tabDetails);
  const isUserRolesPage = location.pathname.includes("/core/user-roles");
  const isProd = process.env.REACT_APP_ENVT === 'PROD';
  //const isUserRolesPage = location.pathname === "/core/user-roles/new-request";
  useLayoutEffect(() => {
    let animationFrameId;
    const measure = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        if (height > 0) {
          setHeaderHeight(height);
        } else {
          animationFrameId = window.requestAnimationFrame(measure);
        }
      } else {
        animationFrameId = window.requestAnimationFrame(measure);
      }
    };
    animationFrameId = window.requestAnimationFrame(measure);
    window.addEventListener("resize", measure);

    return () => {
      window.removeEventListener("resize", measure);
      window.cancelAnimationFrame(animationFrameId);
    };
  }, [tabDetails]);

  const handleMenuItemClick = (path, label, item) => {
    if (path === appRootUrl) {
      window.location.href = path;
      return;
    }
    navigate(path, { state: { label: item.label } });
    dispatch(setTitle(item));
  };

  useEffect(() => {
    const foundItem = controlTowerMenuItems?.find((item) => item.path === location.pathname);
    if (foundItem) {
      dispatch(setTitle(foundItem));
    }
  }, [dispatch, location.pathname]);

  useEffect(() => {
    if (!isAuthenticated) return;

    const checkUserAccess = async () => {
      try {
        const userAccessResp1 = {
          "ctAppRoles": [
            "CT-DEV-USER",
            "CT-BOM",
            "CT-INVENTORY"
          ],
          "regionPlantMapping": {
            "EMEA": [
              "0120",
              "0125"
            ],
            "AMERICAS": [
              "0047"
            ]
          },
        };
        const userAccessResp = await fetchUserAccess();
        const rolesData = (userAccessResp.ctAppRoles || []).map(r => r.trim().toUpperCase());
        dispatch(setCtAppRoles(rolesData));
        setCtAppRole(userAccessResp.ctAppRoles || []);

        const regions = Object.keys(userAccessResp.regionPlantMapping || {});
        const plants = Object.values(userAccessResp.regionPlantMapping || {}).flat();

        // Local check for role & access
        const hasCTDevRole = process.env.REACT_APP_ENVT === 'DEV' ?
          userAccessResp.ctAppRoles?.includes("CT-DEV-USER") : userAccessResp.ctAppRoles?.includes("CT-USER");
        const hasRegionWithPlants = regions.length > 0 && plants.length > 0;
        const userHasAccess = hasCTDevRole && hasRegionWithPlants;
        //const userHasAccess = hasRegionWithPlants;
        //const userHasAccess = true;
        setHasAccess(userHasAccess);

        if (!userHasAccess && !isUserRolesPage) {               // Navigate only if they do *not* have access
          navigate(`/core/authorize`, { replace: true });
        } else {
          dispatch(setApprovedRegions(regions));
          dispatch(setApprovedPlants(plants));
          dispatch(setRegionPlantMapping(userAccessResp.regionPlantMapping));
        }
      } catch (err) {
        console.error("Access check error:", err);
        setHasAccess(false);
        if (location.pathname !== "/core/user-roles/new-request") {
          navigate("/authorize", { replace: true });
        }
      }
    };
    checkUserAccess();
  }, [isAuthenticated, dispatch, location.pathname, navigate, isUserRolesPage]);

  useEffect(() => {
    const foundItem = controlTowerMenuItems?.find(
      (item) => item.path === location.pathname
    );
    if (foundItem) {
      dispatch(setTitle(foundItem));
    }
  }, [dispatch, location.pathname]);

  if (!isAuthenticated || hasAccess === null) {  // Not logged in or still checking access
    return <Loader />;
  }

  return (
    <div className="App">
      {isUserRolesPage && (
        <>
          <SupportButton onClick={() => setOpenSupport(true)} />
          <SupportTicketDialog
            open={openSupport}
            onClose={() => setOpenSupport(false)}
            //initiatorEmail={'initiatorEmail@email.com'}
          />
        </>
      )}
      <ScrollRestoration />
      <Box
        sx={{
          margin: "auto",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          overflowX: "hidden",
        }}
      >
        <Header headerRef={headerRef} />
        <FilterDrawer />
        <SideDrawer contentHeight={headerHeight} />

        <Box sx={{
          paddingTop: `${headerHeight}px`,
          paddingBottom: 2,
          transition: "padding-top 0.3s ease"
        }}>
          {/* {isUserRolesPage ? <AccessRequestForm /> : */}
          <MainContent contentHeight={headerHeight} handleMenuItemClick={handleMenuItemClick} />
          {/* } */}
        </Box>
        <Footer />
      </Box>
    </div>
  );
  // : (
  //   <div
  //     style={{
  //       display: "flex",
  //       flexDirection: "column",
  //       alignItems: "center",
  //     }}
  //   >
  //     <Loader />
  //   </div>
  // );
}
export default App;